import { CommonHeader } from "@bcc/components/common-header";
import { CustomersStats } from "@bcc/components/customers/customers-stats";
import { CustomersTable } from "@bcc/components/customers/customers-table";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { Download, Filter, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setCustomersViewTestId } from "./customers-view.utils";

interface StatData {
  name: string;
  value: string;
  icon: string;
  iconBg: string;
  iconColor: string;
}

interface CustomerData {
  id: number;
  name: string;
  email: string;
  phone: string;
  orders: number;
  totalSpent: string;
  status: "active" | "inactive";
  tier: "VIP" | "Regular";
}

interface CustomersViewProps {
  statsData: StatData[];
  customersData: CustomerData[];
  testId?: string;
}

export function CustomersView({
  statsData,
  customersData,
  testId,
}: CustomersViewProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId ?? setCustomersViewTestId("container")}
      className="space-y-6"
    >
      <CommonHeader
        title={t("views.customers.title")}
        description={t("views.customers.description")}
        actions={
          <>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              {t("views.customers.export")}
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              {t("views.customers.addCustomer")}
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={t("views.customers.searchPlaceholder")}
              className="pl-10"
            />
          </div>
          <Button variant="outline">
            <Filter className="mr-2 h-4 w-4" />
            {t("common.filter")}
          </Button>
        </div>
      </CommonHeader>
      <CustomersStats data={statsData} />
      <CustomersTable data={customersData} />
    </div>
  );
}
